import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

interface ModuleCompletion {
  academy_id: string;
  module_id: number;
  completed_at: string;
}

interface AcademyProgress {
  academy_id: string;
  completed_modules: number;
  total_modules: number;
}

export const useModuleProgress = () => {
  const { user } = useAuth();
  const [completedModules, setCompletedModules] = useState<ModuleCompletion[]>([]);
  const [academyProgress, setAcademyProgress] = useState<AcademyProgress[]>([]);
  const [clientId, setClientId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch client ID and completions
  useEffect(() => {
    const fetchProgress = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      // Get client ID
      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (clientError || !clientData) {
        setIsLoading(false);
        return;
      }

      setClientId(clientData.id);

      // Fetch module completions
      const { data: completions, error: completionsError } = await supabase
        .from('client_module_completions')
        .select('academy_id, module_id, completed_at')
        .eq('client_id', clientData.id);

      if (!completionsError && completions) {
        setCompletedModules(completions);
      }

      // Fetch academy progress
      const { data: progress, error: progressError } = await supabase
        .from('client_academy_progress')
        .select('academy_id, completed_modules, total_modules')
        .eq('client_id', clientData.id);

      if (!progressError && progress) {
        setAcademyProgress(progress);
      }

      setIsLoading(false);
    };

    fetchProgress();
  }, [user]);

  const isModuleCompleted = (academyId: string, moduleId: number): boolean => {
    return completedModules.some(
      (c) => c.academy_id === academyId && c.module_id === moduleId
    );
  };

  const getAcademyProgress = (academyId: string): { completed: number; total: number } => {
    const progress = academyProgress.find((p) => p.academy_id === academyId);
    return {
      completed: progress?.completed_modules || 0,
      total: progress?.total_modules || 10,
    };
  };

  const toggleModuleCompletion = async (academyId: string, moduleId: number): Promise<boolean> => {
    if (!clientId) {
      toast.error('Please log in to track progress');
      return false;
    }

    const isCurrentlyCompleted = isModuleCompleted(academyId, moduleId);

    if (isCurrentlyCompleted) {
      // Remove completion
      const { error } = await supabase
        .from('client_module_completions')
        .delete()
        .eq('client_id', clientId)
        .eq('academy_id', academyId)
        .eq('module_id', moduleId);

      if (error) {
        toast.error('Failed to update progress');
        return false;
      }

      // Update local state
      setCompletedModules((prev) =>
        prev.filter((c) => !(c.academy_id === academyId && c.module_id === moduleId))
      );

      // Update academy progress in database
      const currentProgress = academyProgress.find((p) => p.academy_id === academyId);
      if (currentProgress) {
        const newCount = Math.max(0, currentProgress.completed_modules - 1);
        await supabase
          .from('client_academy_progress')
          .update({ completed_modules: newCount, updated_at: new Date().toISOString() })
          .eq('client_id', clientId)
          .eq('academy_id', academyId);

        setAcademyProgress((prev) =>
          prev.map((p) =>
            p.academy_id === academyId
              ? { ...p, completed_modules: newCount }
              : p
          )
        );
      }

      toast.success('Module marked as incomplete');
      return true;
    } else {
      // Add completion
      const { error } = await supabase
        .from('client_module_completions')
        .insert({
          client_id: clientId,
          academy_id: academyId,
          module_id: moduleId,
        });

      if (error) {
        toast.error('Failed to update progress');
        return false;
      }

      // Update local state
      setCompletedModules((prev) => [
        ...prev,
        { academy_id: academyId, module_id: moduleId, completed_at: new Date().toISOString() },
      ]);

      // Update academy progress in database
      const currentProgress = academyProgress.find((p) => p.academy_id === academyId);
      if (currentProgress) {
        const newCount = currentProgress.completed_modules + 1;
        await supabase
          .from('client_academy_progress')
          .update({ completed_modules: newCount, updated_at: new Date().toISOString() })
          .eq('client_id', clientId)
          .eq('academy_id', academyId);

        setAcademyProgress((prev) =>
          prev.map((p) =>
            p.academy_id === academyId
              ? { ...p, completed_modules: newCount }
              : p
          )
        );
      } else {
        // If no progress record exists, create one (shouldn't happen but fallback)
        setAcademyProgress((prev) => [
          ...prev,
          { academy_id: academyId, completed_modules: 1, total_modules: 10 },
        ]);
      }

      toast.success('Module completed!');
      return true;
    }
  };

  return {
    isLoading,
    completedModules,
    academyProgress,
    isModuleCompleted,
    getAcademyProgress,
    toggleModuleCompletion,
  };
};
